import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';

const VARIANTS = {
  blue: {
    gradient: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
    gradientDark: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    iconBg: 'rgba(25, 118, 210, 0.2)',
    iconBgDark: 'rgba(25, 118, 210, 0.3)',
    iconColor: '#1976D2',
    iconColorDark: '#64B5F6',
  },
  purple: {
    gradient: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
    gradientDark: 'linear-gradient(135deg, #2d1b69 0%, #1a0d3a 100%)',
    iconBg: 'rgba(123, 31, 162, 0.2)',
    iconBgDark: 'rgba(123, 31, 162, 0.3)',
    iconColor: '#7B1FA2',
    iconColorDark: '#BA68C8',
  },
  green: {
    gradient: 'linear-gradient(135deg, #E8F5E8 0%, #C8E6C9 100%)',
    gradientDark: 'linear-gradient(135deg, #1b5e20 0%, #0d3a0d 100%)',
    iconBg: 'rgba(56, 142, 60, 0.2)',
    iconBgDark: 'rgba(56, 142, 60, 0.3)',
    iconColor: '#388E3C',
    iconColorDark: '#81C784',
  },
  orange: {
    gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
    gradientDark: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)',
    iconBg: 'rgba(245, 124, 0, 0.2)',
    iconBgDark: 'rgba(245, 124, 0, 0.3)',
    iconColor: '#F57C00',
    iconColorDark: '#FFB74D',
  },
  neutral: {
    gradient: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
    gradientDark: 'linear-gradient(135deg, #424242 0%, #303030 100%)',
    iconBg: 'rgba(108, 117, 125, 0.15)',
    iconBgDark: 'rgba(108, 117, 125, 0.3)',
    iconColor: '#6C757D',
    iconColorDark: '#B0BEC5',
  },
};

const StatCard = ({
  title,
  value,
  subtitle,
  icon = 'mdi:chart-line',
  variant = 'blue',
  className = '',
  onClickValue,
  onClickIcon,
}) => {
  const v = VARIANTS[variant] || VARIANTS.blue;
  
  // Estado para controlar o tema e forçar re-renderização
  const [currentTheme, setCurrentTheme] = useState('light');

  // Detectar tema inicial e escutar mudanças
  useEffect(() => {
    // Detectar tema inicial
    const initialTheme = document.documentElement.getAttribute('data-theme') || 'light';
    setCurrentTheme(initialTheme);

    // Escutar mudanças no tema
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme') || 'light';
          setCurrentTheme(newTheme);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Detectar se está em dark mode
  const isDarkMode = currentTheme === 'dark';
  
  return (
    <div className={`card h-100 stat-card ${className}`} style={{
      borderRadius: '12px',
      background: isDarkMode ? 'var(--dark-bg-secondary, #2d2d2d)' : v.gradient,
      border: isDarkMode ? '2px solid var(--dark-border,rgb(200, 23, 23))' : '2px solid #333333',
      boxShadow: isDarkMode ? '0 8px 20px rgba(0, 0, 0, 0.3)' : '0 8px 20px rgba(16, 24, 40, 0.08)'
    }}>
      <div style={{
        padding: '12px 16px',
        margin: '0',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0', 
          margin: '0', 
          width: '100%' 
        }}>
          <div style={{ 
            flex: '1',
            padding: '0',
            margin: '0'
          }}>
            <span style={{ 
              fontSize: '16px', 
              fontWeight: '500', 
              color: isDarkMode ? 'var(--dark-text-secondary, #b3b3b3)' : '#4B5563', 
              margin: '0 0 4px 0',
              padding: '0',
              display: 'block'
            }}>{title}</span>
            <span
              onClick={onClickValue}
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: isDarkMode ? 'var(--dark-text-primary, #333333)' : '#111827',
                display: 'block',
                margin: '0 0 2px 0',
                padding: '0',
                cursor: onClickValue ? 'pointer' : 'default',
              }}
            >{value}</span>
            {subtitle && (
              <small style={{
                fontSize: '11px',
                fontWeight: '500',
                color: isDarkMode ? v.iconColorDark : v.iconColor,
                margin: '0',
                padding: '0'
              }}>{subtitle}</small>
            )}
          </div>
          <div
            onClick={onClickIcon || onClickValue}
            style={{
              backgroundColor: isDarkMode ? v.iconBgDark : v.iconBg,
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0',
              padding: '0',
              flexShrink: '0',
              cursor: (onClickIcon || onClickValue) ? 'pointer' : 'default',
            }}
          >
            <Icon icon={icon} style={{
              fontSize: '20px',
              color: isDarkMode ? v.iconColorDark : v.iconColor
            }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatCard;


